import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';
import { infiniteQueryOptions, normalizeInfiniteQueryData } from '../utils';
import { Representative, RepresentativeForm } from './types';

export const representativesApi = createApi({
  reducerPath: 'representativesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + `/representatives`,
  }),
  tagTypes: ['Representative', 'Representatives'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getRepresentatives: builder.query<Representative[], FiltersGrid>({
      query: params => ({
        url: '/query',
        params,
      }),
      providesTags: () => ['Representative', 'Representatives'],
    }),
    getRepresentativesI: builder.infiniteQuery<
      Representative[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['Representative', 'Representatives'],
    }),
    createRepresentative: builder.mutation<Representative, RepresentativeForm>({
      query: data => ({
        url: '/',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['Representative'],
    }),
    getRepresentativeById: builder.query<Representative, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['Representative', 'Representative'],
    }),
    getRepresentativeByIdentityId: builder.query<Representative, string>({
      query: identityId => ({
        url: `/by-identity-id`,
        params: { identityId },
      }),
      providesTags: () => ['Representative', 'Representative'],
    }),
    updateRepresentativeById: builder.mutation<
      RepresentativeForm,
      Representative
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Representative'],
    }),
    deleteRepresentativeById: builder.mutation<Representative, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Representatives'],
    }),
    representativenSendActivationRequest: builder.mutation<
      Representative,
      string
    >({
      query: id => ({
        url: `/${id}/send-activation-request`,
        method: 'POST',
      }),
      invalidatesTags: ['Representative', 'Representatives'],
    }),
  }),
});

export const {
  useGetRepresentativesQuery,
  useGetRepresentativeByIdQuery,
  useCreateRepresentativeMutation,
  useUpdateRepresentativeByIdMutation,
  useDeleteRepresentativeByIdMutation,
  useRepresentativenSendActivationRequestMutation,
  useGetRepresentativeByIdentityIdQuery,
  useLazyGetRepresentativeByIdentityIdQuery,
} = representativesApi;

export const useGetContractsInfiniteQuery = normalizeInfiniteQueryData(
  representativesApi.useGetRepresentativesIInfiniteQuery,
);
